import { IMPORT_DATE_FORMATS } from '@dadohr/shared/domainTypes/time';
import { Checkbox, Chip, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useUnsafeTranslation } from '../../i18next';
import { CustomInputLabel } from '../CustomInputLabel';

type DateFormatOption = {
  value: string;
  label: string;
};

const DATE_FORMAT_OPTIONS: DateFormatOption[] = IMPORT_DATE_FORMATS.map(
  (f) => ({
    label: f.label,
    value: f.value
  })
);

const MONTH_FIRST_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM/DD',
  'MM-DD',
  'MM.DD'
];

const DAY_FIRST_FORMATS = ['DD/MM/YYYY', 'DD.MM.YYYY', 'DD.MM', 'DD/MM', 'DD-MM'];

/**
 * Returns the formats that should be disabled based on currently selected formats
 */
const getDisabledFormats = (selectedFormats: string[]): Set<string> => {
  const disabled = new Set<string>();

  const hasMonthFirst = selectedFormats.some((f) =>
    MONTH_FIRST_FORMATS.includes(f)
  );
  const hasDayFirst = selectedFormats.some((f) =>
    DAY_FIRST_FORMATS.includes(f)
  );

  if (hasMonthFirst) {
    DAY_FIRST_FORMATS.forEach((f) => disabled.add(f));
  }

  if (hasDayFirst) {
    MONTH_FIRST_FORMATS.forEach((f) => disabled.add(f));
  }

  return disabled;
};

interface DateFormatPickerProps {
  value: string[];
  onChange: (formats: string[]) => void;
  label?: string;
  subLabel?: string;
}

export const DateFormatPicker = ({
  value,
  onChange,
  label,
  subLabel
}: DateFormatPickerProps) => {
  const { t } = useUnsafeTranslation();

  const selectedOptions = DATE_FORMAT_OPTIONS.filter((opt) =>
    value.includes(opt.value)
  );

  const disabledFormats = getDisabledFormats(value);

  const defaultLabel = t('Accepted Date Formats');
  const defaultSubLabel = t(
    'Select the date formats that your data source may contain. If none selected, the organization default will be used.'
  );

  return (
    <CustomInputLabel
      label={label || defaultLabel}
      subLabel={subLabel || defaultSubLabel}
      fullWidth
    >
      <Autocomplete
        multiple
        options={DATE_FORMAT_OPTIONS}
        value={selectedOptions}
        onChange={(_event, newValue) => {
          onChange(newValue.map((opt) => opt.value));
        }}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, val) => option.value === val.value}
        getOptionDisabled={(option) => disabledFormats.has(option.value)}
        disableCloseOnSelect
        renderOption={(option, { selected }) => {
          const isDisabled = disabledFormats.has(option.value);
          const content = (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                opacity: isDisabled ? 0.5 : 1
              }}
            >
              <Checkbox
                color="primary"
                checked={selected}
                disabled={isDisabled}
                style={{ marginRight: 8 }}
              />
              {option.label}
            </div>
          );

          return content;
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              label={option.value}
              size="small"
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              selectedOptions.length === 0
                ? t('Using organization default')
                : undefined
            }
          />
        )}
        fullWidth
      />
    </CustomInputLabel>
  );
};
